import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Bot,
  ArrowLeft,
  Activity,
  Users,
  Link2,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  BarChart2,
  Server,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  Shield,
  Ban,
  Search,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { GatewayAgentsApi, GatewayProfilesApi } from "../Api/gatewayApi";
import {
  formatTimestamp,
  timeAgo,
  callerTypeLabel,
  approvalStatusColor,
  connectionStatusColor,
  apiError,
} from "../utils/gatewayUtils";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayTimeline from "../components/GatewayTimeline";
import GatewayFilterBar from "../components/GatewayFilterBar";
import { useToast } from "../../../../hooks/use-toast";
import { cn } from "../../../lib/utils";

function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}


interface Agent {
  id: string;
  agentName?: string;
  agentVersion?: string;
  protocolVersion?: string;
  approvalStatus?: string;
  connectedSessions?: number;
  totalSessions?: number;
  totalRequests?: number;
  firstSeenAt?: string;
  lastSeenAt?: string;
  authClientId?: string;
  capabilities?: any;
}

interface AgentSummary {
  totalAgents?: number;
  activeAgents?: number;
  connectedSessions?: number;
  totalRequests?: number;
  pendingApproval?: number;
}

interface Session {
  sessionId: string;
  authMethod?: string;
  tokenType?: string;
  calledByName?: string;
  userIdentity?: string;
  humanUserName?: string;
  nhiServiceName?: string;
  nhiClientId?: string;
  connectedAt?: string;
  disconnectedAt?: string;
  requestCount?: number;
  lastRequestAt?: string;
  ipAddress?: string;
  status?: string;
}

interface LiveSession {
  sessionId: string;
  agentName?: string;
  agentVersion?: string;
  authMethod?: string;
  connectedDurationMs?: number;
  idleDurationMs?: number;
  requestCount?: number;
  lastRequestAt?: string;
}

interface LiveSessionsData {
  connectedSessions?: LiveSession[];
  totalConnected?: number;
  totalAgentsOnline?: number;
}

interface AnalyticsData {
  totalRequests?: number;
  errorRate?: number;
  avgLatencyMs?: number;
  p95LatencyMs?: number;
  requestsByHour?: Array<{ hour: string; count: number }>;
  errorBreakdown?: Array<{ errorCode?: number; errorMessage?: string; count: number }>;
  toolUsage?: Array<{ toolName: string; serverName?: string; count: number; avgLatencyMs?: number }>;
  serverUsage?: Array<{ serverName: string; requestCount: number; errorCount: number }>;
}

interface AccessData {
  assignedProfiles?: Array<{ id: string; name: string; description?: string }>;
  serverBreakdown?: Record<
    string,
    { allowedTools?: number; totalTools?: number; allowedPrompts?: number; totalPrompts?: number; allowedResources?: number; totalResources?: number }
  >;
}

interface TimelineEvent {
  id?: string;
  timestamp: string;
  eventType?: string;
  module?: string;
  status: string;
  durationMs?: number;
  duration?: number;
  capabilityName?: string;
  serverName?: string;
  serverConfigName?: string;
  correlationId?: string;
  errorMessage?: string;
}

interface TimelineData {
  session?: Partial<Session> & { agentName?: string };
  timeline?: TimelineEvent[];
  totalPages?: number;
  totalElements?: number;
}


function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

function normalizeHourlyForChart(
  raw: Array<{ hour: string; count: number }> = []
): Array<{ label: string; count: number }> {
  const map = new Map<string, number>();
  raw.forEach((h) => {
    if (h.hour) {
      const d = new Date(h.hour);
      if (!isNaN(d.getTime())) {
        d.setMinutes(0, 0, 0);
        const key = d.toISOString().slice(0, 13);
        map.set(key, (map.get(key) || 0) + (h.count || 0));
      }
    }
  });

  const result: Array<{ label: string; count: number }> = [];
  const now = new Date();
  now.setMinutes(0, 0, 0);
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600000);
    const key = d.toISOString().slice(0, 13);
    result.push({
      label: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
      count: map.get(key) || 0,
    });
  }
  return result;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
  }
}


function LoadingSpinner({ text = "Loading…" }: { text?: string }) {
  return (
    <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
      <div className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-2">
      <Bot className="w-10 h-10 text-muted-foreground/70" />
      <p className="text-muted-foreground text-sm font-medium text-center">{message}</p>
      {sub && <p className="text-xs text-muted-foreground text-center">{sub}</p>}
    </div>
  );
}

function ApprovalBadge({ status }: { status?: string }) {
  return (
    <Badge
      className={cn(
        "text-[10px] border font-medium px-2 py-0",
        approvalStatusColor(status || "")
      )}
    >
      {status || "-"}
    </Badge>
  );
}

function ConnectionBadge({ connected }: { connected: boolean }) {
  return (
    <Badge
      className={cn(
        "flex items-center gap-1 text-xs border",
        connected
          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
          : "bg-red-500/15 text-red-400 border-red-500/30"
      )}
    >
      <span
        className={cn(
          "w-1.5 h-1.5 rounded-full",
          connected ? "bg-emerald-400" : "bg-red-400"
        )}
      />
      {connected ? "Connected" : "Disconnected"}
    </Badge>
  );
}

function StatPill({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-2.5 py-1.5 flex-1 min-w-0">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground leading-none mb-0.5">{label}</p>
        <p
          className={cn(
            "text-sm font-semibold leading-none",
            highlight ? "text-emerald-400" : "text-foreground"
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}


function AgentCard({
  agent,
  onSelect,
}: {
  agent: Agent;
  onSelect: (id: string) => void;
}) {
  const connected = agent.connectedSessions || 0;
  const isConnected = connected > 0;
  const shortId = `AN-${agent.id.substring(0, 4).toUpperCase()}-${agent.id.substring(4, 6).toUpperCase()}`;

  return (
    <Card
      className={cn(
        "bg-card hover:bg-card transition-all cursor-pointer group",
        "border-border hover:border-primary/30"
      )}
      onClick={() => onSelect(agent.id)}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 shrink-0 shadow-sm">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-foreground leading-tight">
              {agent.agentName || "Unnamed Agent"}
            </p>
            {agent.agentVersion && (
              <p className="text-xs text-muted-foreground mt-0.5">v{agent.agentVersion}</p>
            )}
          </div>
          <span className="shrink-0 flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              {isConnected && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50 animate-ping" />}
              <span className={cn("relative inline-flex h-2 w-2 rounded-full", isConnected ? "bg-emerald-400" : "bg-red-400")} />
            </span>
            <span className={cn("text-[10px] font-medium", isConnected ? "text-emerald-400" : "text-red-400")}>
              {isConnected ? "Connected" : "Disconnected"}
            </span>
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="bg-muted/70 rounded-lg px-3 py-2.5 border border-border text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Sessions</p>
            <p className="text-xl font-bold text-foreground">{agent.totalSessions || 0}</p>
          </div>
          <div className="bg-muted/70 rounded-lg px-3 py-2.5 border border-border text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Requests</p>
            <p className="text-xl font-bold text-foreground">{agent.totalRequests || 0}</p>
          </div>
          <div className={cn("rounded-lg px-3 py-2.5 border text-center", connected > 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-muted/70 border-border")}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Live</p>
            <p className={cn("text-xl font-bold", connected > 0 ? "text-emerald-400" : "text-foreground")}>{connected}</p>
          </div>
        </div>

        <div className="border-t border-border pt-2.5">
          <div className="flex items-center justify-between py-1.5">
            <span className="text-[11px] text-muted-foreground">Status</span>
            <ApprovalBadge status={agent.approvalStatus} />
          </div>
          {agent.protocolVersion && (
            <div className="flex items-center justify-between py-1.5">
              <span className="text-[11px] text-muted-foreground">Protocol Version</span>
              <span className="text-[11px] text-foreground font-mono">v{agent.protocolVersion}</span>
            </div>
          )}
          <div className="flex items-center justify-between py-1.5">
            <span className="text-[11px] text-muted-foreground">First Seen</span>
            <span className="text-[11px] text-foreground">{timeAgo(agent.firstSeenAt)}</span>
          </div>
          <div className="flex items-center justify-between py-1.5">
            <span className="text-[11px] text-muted-foreground">Last Seen</span>
            <span className="text-[11px] text-foreground">{timeAgo(agent.lastSeenAt)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function OverviewTab({ agent }: { agent: Agent }) {
  const [expanded, setExpanded] = useState(false);
  const caps = agent.capabilities?.capabilities || {};
  const capKeys = Object.keys(caps);

  return (
    <div className="space-y-6 pt-4">
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm text-foreground/80">Agent Metadata</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground mb-0.5">Protocol Version</p>
              <p className="text-foreground font-mono">{agent.protocolVersion || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Agent Version</p>
              <p className="text-foreground font-mono">{agent.agentVersion || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Approval Status</p>
              <ApprovalBadge status={agent.approvalStatus} />
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">First Seen</p>
              <p className="text-foreground">{formatTimestamp(agent.firstSeenAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Last Seen</p>
              <p className="text-foreground">{formatTimestamp(agent.lastSeenAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Total Sessions</p>
              <p className="text-foreground">{agent.totalSessions || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Total Requests</p>
              <p className="text-foreground">{agent.totalRequests || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {capKeys.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-foreground/80 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            Declared Capabilities
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {capKeys.map((key) => {
              const val = caps[key];
              const hasDetail =
                val && typeof val === "object" && Object.keys(val).length > 0;
              return (
                <Card
                  key={key}
                  className="bg-card border-border"
                >
                  <CardContent className="px-3 py-3">
                    <p className="text-xs font-mono text-primary mb-1.5">{key}</p>
                    {hasDetail ? (
                      Object.entries(val).map(([k, v]) => (
                        <p key={k} className="text-xs text-muted-foreground">
                          {k}:{" "}
                          <span className="text-foreground">
                            {typeof v === "object" ? JSON.stringify(v) : String(v)}
                          </span>
                        </p>
                      ))
                    ) : (
                      <p className="text-xs text-emerald-400">Supported</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {agent.capabilities && (
        <Card className="bg-card border-border">
          <CardHeader
            className="pb-2 pt-4 px-4 cursor-pointer"
            onClick={() => setExpanded((p) => !p)}
          >
            <div className="flex items-center gap-2">
              {expanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Raw Initialize Payload
              </CardTitle>
            </div>
          </CardHeader>
          {expanded && (
            <CardContent className="px-4 pb-4">
              <pre className="text-xs text-foreground/80 bg-background/60 rounded-lg p-3 overflow-x-auto font-mono leading-relaxed max-h-64">
                {JSON.stringify(agent.capabilities, null, 2)}
              </pre>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}


function CapabilityAccessTab({
  agentId,
  agentName,
}: {
  agentId: string;
  agentName: string;
}) {
  const { toast } = useToast();
  const [accessData, setAccessData] = useState<AccessData | null>(null);
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [access, profiles] = await Promise.all([
        GatewayProfilesApi.agentAccess(agentId),
        GatewayProfilesApi.list().catch(() => []),
      ]);
      setAccessData(access);
      setAllProfiles(Array.isArray(profiles) ? profiles : []);
    } catch (e: any) {
      toast({
        title: "Failed to load access data",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingSpinner text="Loading capability access…" />;

  const profiles = accessData?.assignedProfiles || [];
  const serverBreakdown = accessData?.serverBreakdown || {};
  const assignedIds = new Set(profiles.map((p) => p.id));
  const unassigned = allProfiles.filter((p) => !assignedIds.has(p.id));

  const handleAssign = async () => {
    if (!selectedProfileId) {
      toast({ title: "Select a profile first", variant: "destructive" });
      return;
    }
    try {
      await GatewayProfilesApi.assign(selectedProfileId, agentId);
      toast({ title: "Profile assigned" });
      setSelectedProfileId("");
      load();
    } catch (e: any) {
      toast({ title: "Assignment failed", description: apiError(e), variant: "destructive" });
    }
  };

  const handleUnassign = async (profileId: string) => {
    try {
      await GatewayProfilesApi.unassign(profileId, agentId);
      toast({ title: "Profile removed" });
      load();
    } catch (e: any) {
      toast({ title: "Unassignment failed", description: apiError(e), variant: "destructive" });
    }
  };

  const serverNames = Object.keys(serverBreakdown);

  return (
    <div className="space-y-6 pt-4">
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm text-foreground/80">
            Assigned Profiles ({profiles.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          {profiles.length === 0 ? (
            <div className="text-center py-6 space-y-1">
              <Shield className="w-8 h-8 text-muted-foreground/70 mx-auto" />
              <p className="text-sm text-muted-foreground">No profiles assigned (default deny)</p>
              <p className="text-xs text-muted-foreground">
                This agent cannot see any capabilities. Assign a profile to grant access.
              </p>
            </div>
          ) : (
            profiles.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-muted/70 px-3 py-2.5"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                  {p.description && (
                    <p className="text-xs text-muted-foreground">{p.description}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7"
                  onClick={() => handleUnassign(p.id)}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            ))
          )}

          {unassigned.length > 0 && (
            <div className="flex gap-2 mt-3">
              <select
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                className="flex-1 bg-muted/60 border border-border rounded-md text-sm text-foreground/80 px-3 py-1.5 focus:outline-none focus:border-primary/50"
              >
                <option value="">Select profile to assign…</option>
                {unassigned.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                className="bg-primary/80 hover:bg-primary text-white text-xs"
                onClick={handleAssign}
              >
                Assign
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {serverNames.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Effective Access</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Server</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Tools</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Prompts</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Resources</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serverNames.map((srv) => {
                  const bd = serverBreakdown[srv];
                  return (
                    <TableRow key={srv} className="border-border hover:bg-muted/60">
                      <TableCell className="text-xs text-foreground/80 flex items-center gap-2">
                        <Server className="w-3.5 h-3.5 text-primary shrink-0" />
                        {srv}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedTools ?? 0} / {bd.totalTools ?? 0}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedPrompts ?? 0} / {bd.totalPrompts ?? 0}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedResources ?? 0} / {bd.totalResources ?? 0}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function AnalyticsTab({ agentId }: { agentId: string }) {
  const { toast } = useToast();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GatewayAgentsApi.analytics(agentId, 24)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          toast({
            title: "Failed to load analytics",
            description: apiError(e),
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, toast]);

  if (loading) return <LoadingSpinner text="Loading analytics…" />;

  if (!data || (data.totalRequests || 0) === 0) {
    return (
      <EmptyState
        message="No analytics data available yet"
        sub="Data will appear after the agent makes some requests."
      />
    );
  }

  const hourlyData = normalizeHourlyForChart(data.requestsByHour || []);
  const lastHourCount =
    hourlyData.length ? hourlyData[hourlyData.length - 1].count : 0;
  const errorRate = Number(data.errorRate || 0);
  const avgLatency =
    data.avgLatencyMs != null ? Math.round(data.avgLatencyMs) : null;
  const p95Latency =
    data.p95LatencyMs != null ? Math.round(data.p95LatencyMs) : null;

  const errors = data.errorBreakdown || [];
  const tools = data.toolUsage || [];
  const servers = data.serverUsage || [];

  return (
    <div className="space-y-6 pt-4">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <GatewayStatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Total Requests"
          value={data.totalRequests || 0}
          subtitle={`+${lastHourCount} last hr`}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Error Rate"
          value={`${errorRate.toFixed(1)}%`}
          subtitle={errorRate > 0 ? "increasing" : "stable"}
          colorClass={errorRate > 0 ? "text-red-400" : "text-emerald-400"}
        />
        <GatewayStatCard
          icon={<Clock className="w-5 h-5" />}
          label="Avg Latency"
          value={avgLatency != null ? `${avgLatency}ms` : "-"}
          subtitle={avgLatency != null && avgLatency < 1000 ? "stable" : "elevated"}
          colorClass={
            avgLatency != null && avgLatency < 1000
              ? "text-emerald-400"
              : "text-amber-400"
          }
        />
        <GatewayStatCard
          icon={<Zap className="w-5 h-5" />}
          label="P95 Latency"
          value={p95Latency != null ? `${p95Latency}ms` : "-"}
          subtitle={p95Latency != null && p95Latency < 1500 ? "improving" : "slower"}
          colorClass={
            p95Latency != null && p95Latency < 1500
              ? "text-emerald-400"
              : "text-red-400"
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-foreground/80">
                Requests by Hour (24h)
              </CardTitle>
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border text-xs">
                Live Traffic
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">Distribution of incoming traffic volume</p>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={hourlyData}
                margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="agentAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: "#64748b" }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#64748b" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#94a3b8" }}
                  itemStyle={{ color: "#22d3ee" }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#06b6d4"
                  strokeWidth={1.5}
                  fill="url(#agentAreaGrad)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Error Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {errors.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500/40" />
                <p className="text-xs text-muted-foreground">No errors captured in selected window</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Error Type</TableHead>
                      <TableHead className="text-xs text-muted-foreground text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((e, idx) => (
                      <TableRow key={idx} className="border-border hover:bg-muted/60">
                        <TableCell className="py-2">
                          <p className="text-xs font-mono text-red-400">
                            {e.errorCode != null ? `Error ${e.errorCode}` : "Unknown Error"}
                          </p>
                          {e.errorMessage && (
                            <p className="text-xs text-muted-foreground mt-0.5">{e.errorMessage}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          <Badge className="bg-red-500/15 text-red-400 border-red-500/30 border text-xs">
                            {e.count}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Tool Usage Statistics</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {tools.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No tool usage yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Tool</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Calls</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Avg Latency</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tools.map((t, idx) => (
                      <TableRow key={idx} className="border-border hover:bg-muted/60">
                        <TableCell className="py-2">
                          <p className="text-xs font-mono text-primary">{t.toolName || "-"}</p>
                          {t.serverName && (
                            <p className="text-xs text-muted-foreground mt-0.5">{t.serverName}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-foreground/80 py-2">
                          {t.count || 0} call{(t.count || 0) !== 1 ? "s" : ""}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground py-2">
                          {t.avgLatencyMs != null ? `${Math.round(t.avgLatencyMs)}ms` : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Server Usage</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {servers.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No server usage yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Server</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Requests</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {servers.map((s, idx) => {
                      const err = s.errorCount || 0;
                      return (
                        <TableRow key={idx} className="border-border hover:bg-muted/60">
                          <TableCell className="text-xs text-foreground/80 py-2">
                            {s.serverName || "-"}
                          </TableCell>
                          <TableCell className="text-xs text-foreground/80 py-2">
                            {s.requestCount || 0}
                          </TableCell>
                          <TableCell className="py-2">
                            {err > 0 ? (
                              <Badge className="bg-red-500/15 text-red-400 border-red-500/30 border text-xs">
                                {err}
                              </Badge>
                            ) : (
                              <span className="text-xs text-emerald-400">0</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


interface PagedSessions {
  content: any[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface AgentSessionFilterValues {
  status: string[];
  authMethod: string[];
  tokenType: string[];
  callerType: string[];
  ipAddress: string;
  fromDate: string;
  toDate: string;
  search: string;
}

const EMPTY_AGENT_SESSION_FILTERS: AgentSessionFilterValues = {
  status: [],
  authMethod: [],
  tokenType: [],
  callerType: [],
  ipAddress: "",
  fromDate: "",
  toDate: "",
  search: "",
};

function SessionsTab({
  agentId,
  currentAgent,
}: {
  agentId: string;
  currentAgent: Agent | null;
}) {
  const { toast } = useToast();
  const [paged, setPaged] = useState<PagedSessions>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 15,
  });
  const [loading, setLoading] = useState(true);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelinePage, setTimelinePage] = useState(0);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [timelineTotalPages, setTimelineTotalPages] = useState(0);
  const [timelineTotalElements, setTimelineTotalElements] = useState(0);

  const [draftFilters, setDraftFilters] = useState<AgentSessionFilterValues>(EMPTY_AGENT_SESSION_FILTERS);
  const [filters, setFilters] = useState<AgentSessionFilterValues>(EMPTY_AGENT_SESSION_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [sort] = useState("connectedAt,desc");
  const [filterOptions, setFilterOptions] = useState<{
    statuses: string[];
    callerTypes: string[];
    authMethods: string[];
    tokenTypes: string[];
  }>({ statuses: [], callerTypes: [], authMethods: [], tokenTypes: [] });

  useEffect(() => {
    let cancelled = false;
    GatewayAgentsApi.sessionFilters(agentId)
      .then((data: any) => {
        if (cancelled) return;
        setFilterOptions({
          statuses: data?.statuses || [],
          callerTypes: data?.callerTypes || [],
          authMethods: data?.authMethods || [],
          tokenTypes: data?.tokenTypes || [],
        });
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    setPage(0);
  }, [filters, size]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: any = {
      page,
      size,
      sort,
    };
    if (filters.status.length) params.status = filters.status;
    if (filters.authMethod.length) params.authMethod = filters.authMethod;
    if (filters.tokenType.length) params.tokenType = filters.tokenType;
    if (filters.callerType.length) params.callerType = filters.callerType;
    if (filters.ipAddress) params.ipAddress = filters.ipAddress;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.search) params.search = filters.search;

    GatewayAgentsApi.sessions(agentId, params)
      .then((data: any) => {
        if (cancelled) return;
        setPaged({
          content: Array.isArray(data?.content) ? data.content : [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          number: typeof data?.number === "number" ? data.number : 0,
          size: typeof data?.size === "number" ? data.size : size,
        });
      })
      .catch((e: Error) => {
        if (!cancelled) {
          toast({
            title: "Failed to load sessions",
            description: apiError(e),
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, page, size, sort, filters]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value } as AgentSessionFilterValues));
  };
  const handleApplyFilters = () => {
    setFilters(draftFilters);
  };
  const handleClearFilters = () => {
    setDraftFilters(EMPTY_AGENT_SESSION_FILTERS);
    setFilters(EMPTY_AGENT_SESSION_FILTERS);
  };

  const sessions = paged.content as Session[];
  const totalElements = paged.totalElements;
  const totalPages = paged.totalPages;
  const currentPage = paged.number;
  const pageSize = paged.size || size;
  const from = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalElements);

  const loadTimeline = useCallback(
    async (sessionId: string, page: number, reset: boolean) => {
      setTimelineLoading(true);
      try {
        const data: TimelineData = await GatewayAgentsApi.sessionTimeline(
          sessionId,
          page,
          50
        );
        const events: TimelineEvent[] = (data.timeline || []).map((e) => ({
          ...e,
          id: e.id || `${e.timestamp}-${Math.random()}`,
          duration: e.durationMs,
          serverConfigName: e.serverName,
        }));
        if (reset) {
          setTimelineEvents(events);
        } else {
          setTimelineEvents((prev) => [...prev, ...events]);
        }
        setTimelineTotalPages(data.totalPages || 0);
        setTimelineTotalElements(data.totalElements || 0);
        setTimelinePage(page);
      } catch (e: any) {
        toast({
          title: "Failed to load timeline",
          description: apiError(e),
          variant: "destructive",
        });
      } finally {
        setTimelineLoading(false);
      }
    },
    [toast]
  );

  const handleToggleTimeline = async (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      setTimelineEvents([]);
      return;
    }
    setExpandedSessionId(sessionId);
    setTimelineEvents([]);
    await loadTimeline(sessionId, 0, true);
  };

  const handleLoadMore = async () => {
    if (!expandedSessionId) return;
    await loadTimeline(expandedSessionId, timelinePage + 1, false);
  };

  return (
    <div className="space-y-4 pt-4">
      <GatewayFilterBar
        fields={[
          { key: "status", label: "Status", type: "multiselect", options: filterOptions.statuses },
          { key: "callerType", label: "Caller Type", type: "multiselect", options: filterOptions.callerTypes },
          { key: "authMethod", label: "Auth Method", type: "multiselect", options: filterOptions.authMethods },
          { key: "tokenType", label: "Token Type", type: "multiselect", options: filterOptions.tokenTypes },
          { key: "ipAddress", label: "IP Address", type: "ip", placeholder: "e.g. 192.168.1.* or 10.0.0.5" },
          { key: "fromDate", label: "From", type: "date" },
          { key: "toDate", label: "To", type: "date" },
          { key: "search", label: "Search", type: "text", placeholder: "Search sessions…" },
        ]}
        values={draftFilters as unknown as Record<string, string | string[]>}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <p className="text-sm text-muted-foreground">
        Session History ({totalElements.toLocaleString()}){" "}
        <span className="text-muted-foreground text-xs">— click a row to view timeline</span>
      </p>

      {loading ? (
        <LoadingSpinner text="Loading sessions…" />
      ) : sessions.length === 0 ? (
        <EmptyState
          message="No sessions match the current filters"
          sub="Try clearing filters or adjusting the date range."
        />
      ) : (
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground min-w-[200px]">Session ID</TableHead>
              <TableHead className="text-xs text-muted-foreground">Auth</TableHead>
              <TableHead className="text-xs text-muted-foreground">Called By</TableHead>
              <TableHead className="text-xs text-muted-foreground">Caller Type</TableHead>
              <TableHead className="text-xs text-muted-foreground">Connected</TableHead>
              <TableHead className="text-xs text-muted-foreground">Disconnected</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Requests</TableHead>
              <TableHead className="text-xs text-muted-foreground">Last Request</TableHead>
              <TableHead className="text-xs text-muted-foreground">IP Address</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => {
              const isExpanded = expandedSessionId === s.sessionId;
              const { label: callerLabel, color: callerColor } = callerTypeLabel(
                s.tokenType
              );

              let calledByText = "-";
              if (s.tokenType === "HUMAN_DELEGATED") {
                calledByText =
                  s.calledByName || s.userIdentity || s.humanUserName || "-";
              } else if (s.tokenType === "AUTOMATED_AGENT") {
                calledByText = s.calledByName || s.nhiServiceName || "-";
              }

              return (
                <React.Fragment key={s.sessionId}>
                  <TableRow
                    className={cn(
                      "border-border cursor-pointer transition-colors",
                      isExpanded
                        ? "bg-muted/50 hover:bg-muted/60"
                        : "hover:bg-muted/60"
                    )}
                    onClick={() => handleToggleTimeline(s.sessionId)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1.5">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-primary shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        )}
                        <code className="text-xs text-foreground/80 font-mono truncate max-w-[160px]">
                          {s.sessionId || "-"}
                        </code>
                        <button
                          className="shrink-0 text-muted-foreground hover:text-foreground/80 p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(s.sessionId || "");
                            toast({ title: "Session ID copied" });
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 py-2">
                      {s.authMethod || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 py-2">
                      {calledByText}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge
                        className={cn("text-xs border py-0", callerColor)}
                      >
                        {callerLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      {formatTimestamp(s.connectedAt)}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      {s.disconnectedAt ? (
                        <span className="text-muted-foreground">
                          {formatTimestamp(s.disconnectedAt)}
                        </span>
                      ) : (
                        <span className="text-emerald-400">active</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 text-center py-2">
                      {s.requestCount || 0}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      {formatTimestamp(s.lastRequestAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono py-2">
                      {s.ipAddress || "-"}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge
                        className={cn(
                          "text-xs border",
                          s.status === "CONNECTED"
                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                            : "bg-red-500/15 text-red-400 border-red-500/30"
                        )}
                      >
                        {s.status || "-"}
                      </Badge>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow className="border-border hover:bg-transparent">
                      <TableCell colSpan={10} className="py-0">
                        <div className="py-4 px-6 bg-muted/70 border-t border-border">
                          {timelineLoading && timelineEvents.length === 0 ? (
                            <LoadingSpinner text="Loading timeline…" />
                          ) : timelineEvents.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-4">
                              No audit events for this session.
                            </p>
                          ) : (
                            <>
                              <GatewayTimeline events={timelineEvents} />
                              {timelinePage + 1 < timelineTotalPages && (
                                <div className="text-center mt-4">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-border text-muted-foreground text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLoadMore();
                                    }}
                                    disabled={timelineLoading}
                                  >
                                    {timelineLoading ? (
                                      <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin mr-1" />
                                    ) : null}
                                    Load More (
                                    {timelineTotalElements -
                                      (timelinePage + 1) * 50}{" "}
                                    remaining)
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      {!loading && totalElements > 0 && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-card">
          <span className="text-xs text-muted-foreground">
            Showing {from}–{to} of {totalElements.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage + 1} / {Math.max(1, totalPages)}
            </span>
            <button
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <select
              value={pageSize}
              onChange={(e) => setSize(Number(e.target.value))}
              className="ml-2 h-7 bg-card border border-border rounded-md px-2 text-xs text-foreground/80 outline-none"
            >
              {[10, 15, 25, 50].map((s) => (
                <option key={s} value={s}>
                  {s} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}


function LiveSessionsTab() {
  const { toast } = useToast();
  const [data, setData] = useState<LiveSessionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await GatewayAgentsApi.liveSessions();
      setData(result);
    } catch (e: any) {
      toast({
        title: "Failed to load live sessions",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    refreshRef.current = setInterval(load, 5000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [load]);

  if (loading) return <LoadingSpinner text="Loading live sessions…" />;

  const sessions = data?.connectedSessions || [];

  if (sessions.length === 0) {
    return (
      <EmptyState
        message="No active sessions right now"
        sub="Active sessions will appear here and refresh every 5 seconds."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-muted/60 border border-border px-4 py-2 text-center">
          <p className="text-lg font-bold text-foreground">{data?.totalConnected ?? 0}</p>
          <p className="text-xs text-muted-foreground">Connected</p>
        </div>
        <div className="rounded-lg bg-muted/60 border border-border px-4 py-2 text-center">
          <p className="text-lg font-bold text-foreground">
            {data?.totalAgentsOnline ?? 0}
          </p>
          <p className="text-xs text-muted-foreground">Agents Online</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Auto-refreshing every 5s
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Agent</TableHead>
              <TableHead className="text-xs text-muted-foreground min-w-[180px]">Session ID</TableHead>
              <TableHead className="text-xs text-muted-foreground">Auth</TableHead>
              <TableHead className="text-xs text-muted-foreground">Connected For</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Requests</TableHead>
              <TableHead className="text-xs text-muted-foreground">Last Request</TableHead>
              <TableHead className="text-xs text-muted-foreground">Idle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => {
              const idle = s.idleDurationMs || 0;
              const isActive = idle < 60000;
              return (
                <TableRow key={s.sessionId} className="border-border hover:bg-muted/60">
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "w-2 h-2 rounded-full",
                          isActive ? "bg-emerald-400 animate-pulse" : "bg-muted-foreground/50"
                        )}
                      />
                      <span className="text-xs text-foreground/80">
                        {s.agentName || "-"}
                      </span>
                      {s.agentVersion && (
                        <span className="text-xs text-muted-foreground font-mono">
                          v{s.agentVersion}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs text-muted-foreground font-mono">
                        {(s.sessionId || "").substring(0, 24)}…
                      </code>
                      <button
                        className="text-muted-foreground hover:text-foreground/80 p-0.5"
                        onClick={() => {
                          copyToClipboard(s.sessionId || "");
                          toast({ title: "Session ID copied" });
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 py-2">
                    {s.authMethod || "-"}
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 py-2">
                    {formatDurationMs(s.connectedDurationMs)}
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 text-center py-2">
                    {s.requestCount || 0}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-2">
                    {s.lastRequestAt ? formatTimestamp(s.lastRequestAt) : "-"}
                  </TableCell>
                  <TableCell className="text-xs py-2">
                    <span
                      className={cn(
                        idle < 60000 ? "text-emerald-400" : "text-muted-foreground"
                      )}
                    >
                      {formatDurationMs(idle)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function AgentDetailView({
  agentId,
  onBack,
}: {
  agentId: string;
  onBack: () => void;
}) {
  const { toast } = useToast();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const loadAgent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GatewayAgentsApi.get(agentId);
      setAgent(data);
    } catch (e: any) {
      toast({
        title: "Failed to load agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const handleApprove = async () => {
    if (!agent) return;
    setApproving(true);
    try {
      const res = await GatewayAgentsApi.approve(agentId);
      toast({ title: res.message || "Agent approved" });
      loadAgent();
    } catch (e: any) {
      toast({
        title: "Failed to approve agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleBlock = async () => {
    if (!agent) return;
    setBlocking(true);
    try {
      const res = await GatewayAgentsApi.block(agentId);
      const parts = [res.message || "Agent blocked"];
      if (res.sessionsTerminated > 0)
        parts.push(`${res.sessionsTerminated} session(s) terminated`);
      toast({ title: parts.join(" · ") });
      loadAgent();
    } catch (e: any) {
      toast({
        title: "Failed to block agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setBlocking(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading agent details…" />;
  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <p className="text-muted-foreground text-sm">Agent not found</p>
        <Button size="sm" variant="outline" onClick={onBack}>
          Back to Agents
        </Button>
      </div>
    );
  }

  const connected = (agent.connectedSessions || 0) > 0;
  const approvalStatus = agent.approvalStatus || "APPROVED";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Agents
        </Button>

        <div className="flex items-center gap-2">
          {(approvalStatus === "PENDING" || approvalStatus === "BLOCKED") && (
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
              onClick={handleApprove}
              disabled={approving}
            >
              {approving ? (
                <div className="w-3 h-3 border border-emerald-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              APPROVE
            </button>
          )}
          {(approvalStatus === "PENDING" || approvalStatus === "APPROVED") && (
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
              onClick={handleBlock}
              disabled={blocking}
            >
              {blocking ? (
                <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Ban className="w-3.5 h-3.5" />
              )}
              BLOCK
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
        <div className="relative w-14 h-14 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-primary to-blue-600">
          <Bot className="w-7 h-7 text-white" />
          <span
            className={cn(
              "absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-border",
              connected ? "bg-emerald-400" : "bg-red-400"
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-foreground">
              {agent.agentName || "Unnamed Agent"}
            </h2>
            <ApprovalBadge status={agent.approvalStatus} />
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {agent.agentVersion ? `v${agent.agentVersion}` : ""}
            {agent.authClientId ? ` · ${agent.authClientId}` : ""}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted/60 border border-border">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="access"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground"
          >
            Capability Access
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <BarChart2 className="w-3.5 h-3.5" />
            Analytics
          </TabsTrigger>
          <TabsTrigger
            value="sessions"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground"
          >
            Sessions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab agent={agent} />
        </TabsContent>
        <TabsContent value="access">
          <CapabilityAccessTab
            agentId={agentId}
            agentName={agent.agentName || ""}
          />
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsTab agentId={agentId} />
        </TabsContent>
        <TabsContent value="sessions">
          <SessionsTab agentId={agentId} currentAgent={agent} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


export default function GatewayAgentsPage() {
  const { toast } = useToast();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [summary, setSummary] = useState<AgentSummary>({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<"list" | "detail">("list");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [activeListTab, setActiveListTab] = useState<"agents" | "live">("agents");
  const [agentSearch, setAgentSearch] = useState("");
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAgents = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([
        GatewayAgentsApi.list(),
        GatewayAgentsApi.summary(),
      ]);
      setAgents(Array.isArray(list) ? list : []);
      setSummary(s || {});
    } catch (e: any) {
      toast({
        title: "Failed to load agents",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAgents();
    refreshRef.current = setInterval(() => {
      if (activeListTab === "agents") loadAgents();
    }, 10000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [loadAgents, activeListTab]);

  useEffect(() => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    if (activeListTab === "agents" && currentView === "list") {
      refreshRef.current = setInterval(loadAgents, 10000);
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [activeListTab, currentView, loadAgents]);

  const handleSelectAgent = (id: string) => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    setSelectedAgentId(id);
    setCurrentView("detail");
  };

  const handleBack = () => {
    setCurrentView("list");
    setSelectedAgentId(null);
    setActiveListTab("agents");
    loadAgents();
    refreshRef.current = setInterval(loadAgents, 10000);
  };

  if (currentView === "detail" && selectedAgentId) {
    return (
      <div className="p-6">
        <AgentDetailView agentId={selectedAgentId} onBack={handleBack} />
      </div>
    );
  }

  const pendingCount = summary.pendingApproval || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            AI Agents
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Discovered agents, sessions, and analytics
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 border text-xs flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              {pendingCount} pending approval
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            className="border-border text-muted-foreground hover:text-foreground"
            onClick={loadAgents}
            disabled={loading}
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-muted-foreground border-t-foreground/80 rounded-full animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <GatewayStatCard
          icon={<Bot className="w-5 h-5" />}
          label="Total Agents"
          value={summary.totalAgents ?? 0}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<Activity className="w-5 h-5" />}
          label="Active"
          value={summary.activeAgents ?? 0}
          colorClass="text-emerald-400"
        />
        <GatewayStatCard
          icon={<Users className="w-5 h-5" />}
          label="Connected Sessions"
          value={summary.connectedSessions ?? 0}
          colorClass="text-violet-400"
        />
        <GatewayStatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Total Requests"
          value={summary.totalRequests ?? 0}
          colorClass="text-sky-400"
        />
      </div>

      <Tabs
        value={activeListTab}
        onValueChange={(v) => setActiveListTab(v as "agents" | "live")}
      >
        <TabsList className="bg-muted/60 border border-border">
          <TabsTrigger
            value="agents"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <Bot className="w-3.5 h-3.5" />
            Agents
            {pendingCount > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 border-none text-xs ml-1 px-1.5 py-0">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="live"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground flex items-center gap-1.5"
          >
            <Activity className="w-3.5 h-3.5" />
            Live Sessions
            {(summary.connectedSessions ?? 0) > 0 && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-none text-xs ml-1 px-1.5 py-0">
                {summary.connectedSessions}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="mt-4">
          {loading ? (
            <LoadingSpinner text="Loading agents…" />
          ) : agents.length === 0 ? (
            <EmptyState
              message="No agents discovered yet"
              sub="Agents are auto-discovered when they connect via MCP."
            />
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search agents by name, version..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-muted/60 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
                  />
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  Auto-refreshing every 10s
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {(() => {
                  if (!agentSearch.trim()) return agents;
                  const q = agentSearch.toLowerCase().trim();
                  const startsWith: typeof agents = [];
                  const contains: typeof agents = [];
                  for (const a of agents) {
                    const name = (a.agentName || "").toLowerCase();
                    if (name.startsWith(q)) startsWith.push(a);
                    else if (name.includes(q) || (a.agentVersion || "").toLowerCase().includes(q)) contains.push(a);
                  }
                  return [...startsWith, ...contains];
                })()
                  .map((agent) => (
                    <AgentCard
                      key={agent.id}
                      agent={agent}
                      onSelect={handleSelectAgent}
                    />
                  ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="live" className="mt-4">
          <LiveSessionsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
